/**
 * Markdown eines Wissensdatenbank-Artikels, in Mantine-Bausteinen.
 *
 * Anders als bei KIAN-Ausgaben (lib/safeMarkdown.ts) schreiben hier Kollegen, und ein Admin
 * gibt frei. Links auf Herstellerdokumentation sind der halbe Wert eines Artikels und bleiben
 * deshalb stehen; `javascript:` und Co. filtert react-markdown selbst. Bilder nicht: Hochladen
 * kann man keine, und ein eingebettetes fremdes Bild meldet jeden Leser an dessen Server.
 */
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Anchor, Blockquote, Box, Code, Divider, List, Table, Text, Title } from '@mantine/core';
import classes from './KnowledgeBaseMarkdown.module.css';

interface HastLike {
  value?: string;
  children?: HastLike[];
}

/** Reiner Text eines Code-Blocks — `<pre><code>` sonst doppelt gerahmt. */
function hastText(node: HastLike | undefined): string {
  if (!node) return '';
  if (typeof node.value === 'string') return node.value;
  return (node.children ?? []).map(hastText).join('');
}

export function KnowledgeBaseMarkdown({ content }: { content: string }) {
  return (
    <Box className={classes.root}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        disallowedElements={['img']}
        unwrapDisallowed
        components={{
          h1: ({ children }) => <Title order={2} mt="lg" mb="xs">{children}</Title>,
          h2: ({ children }) => <Title order={3} mt="lg" mb="xs">{children}</Title>,
          h3: ({ children }) => <Title order={4} mt="md" mb="xs">{children}</Title>,
          h4: ({ children }) => <Title order={5} mt="md" mb="xs">{children}</Title>,
          h5: ({ children }) => <Title order={6} mt="sm" mb="xs">{children}</Title>,
          h6: ({ children }) => <Title order={6} mt="sm" mb="xs">{children}</Title>,
          p: ({ children }) => <Text mb="sm">{children}</Text>,
          a: ({ href, children }) => {
            const external = !!href && /^https?:\/\//i.test(href);
            return (
              <Anchor
                href={href}
                {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              >
                {children}
              </Anchor>
            );
          },
          ul: ({ children }) => <List mb="sm" spacing={4}>{children}</List>,
          ol: ({ children }) => <List type="ordered" mb="sm" spacing={4}>{children}</List>,
          li: ({ children }) => <List.Item>{children}</List.Item>,
          blockquote: ({ children }) => <Blockquote mb="sm" p="sm">{children}</Blockquote>,
          hr: () => <Divider my="md" />,
          pre: ({ node }) => (
            <Code block mb="sm">
              {hastText(node as HastLike).replace(/\n$/, '')}
            </Code>
          ),
          code: ({ children }) => <Code>{children}</Code>,
          table: ({ children }) => (
            <Table.ScrollContainer minWidth={480} mb="sm">
              <Table withTableBorder withColumnBorders striped>{children}</Table>
            </Table.ScrollContainer>
          ),
          thead: ({ children }) => <Table.Thead>{children}</Table.Thead>,
          tbody: ({ children }) => <Table.Tbody>{children}</Table.Tbody>,
          tr: ({ children }) => <Table.Tr>{children}</Table.Tr>,
          th: ({ children }) => <Table.Th>{children}</Table.Th>,
          td: ({ children }) => <Table.Td>{children}</Table.Td>,
        }}
      >
        {content}
      </ReactMarkdown>
    </Box>
  );
}
